import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Auto-scrolling log viewer with scroll detection. Automatically scrolls to
 * bottom unless the user has scrolled up. Extracted out of Canvas.jsx, where
 * it was defined at module scope with no dependency on Canvas's own state.
 */
const LogViewer = ({ logs = [], variant = 'dark' }) => {
  const logContainerRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const previousLogsLength = useRef(0);

  // Auto-scroll to bottom when new logs arrive and auto-scroll is enabled
  useEffect(() => {
    if (shouldAutoScroll && logContainerRef.current && logs.length > previousLogsLength.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
    previousLogsLength.current = logs.length;
  }, [logs, shouldAutoScroll]);

  // Detect user scroll and disable auto-scroll if user scrolls up
  const handleScroll = useCallback(() => {
    if (!logContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10; // 10px threshold

    // Enable auto-scroll only if user is at the bottom
    setShouldAutoScroll(isAtBottom);
  }, []);

  const light = variant === 'light';

  return (
    <div
      ref={logContainerRef}
      onScroll={handleScroll}
      className={light ? 'log-viewer-light' : undefined}
      style={light ? undefined : {
        marginTop: '15px',
        padding: '10px',
        background: '#1e1e1e',
        borderRadius: '4px',
        height: '20vh',
        overflowY: 'auto',
        fontFamily: 'monospace',
        fontSize: '12px',
        lineHeight: '1.4'
      }}
    >
      {logs.map((log, idx) => {
        // Handle both string logs and object logs with timestamp/message
        const logText = typeof log === 'string' ? log : log.message || String(log);
        const timestamp = log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '';

        return (
          light ? (
            <div key={idx} className="log-line">
              {timestamp && <span className="log-time">{timestamp}</span>}{logText}
            </div>
          ) : (
            <div key={idx} style={{ marginBottom: '4px', color: '#ccc' }}>
              {timestamp && `${timestamp}: `}{logText}
            </div>
          )
        );
      })}
    </div>
  );
};

LogViewer.propTypes = {
  logs: PropTypes.array,
  variant: PropTypes.oneOf(['dark', 'light']),
};

export default LogViewer;
